import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthSession, adminApi, categoryApi } from '../services/api';
import DashboardSidebar from '../components/DashboardSidebar';
import '../styles/admin-dashboard.css';

const POPULAR_ICONS = ['👨‍👩‍👧', '💼', '🧠', '👶', '💑', '🌿', '🎯', '🩺', '🤝', '💭', '🌟', '🧘', '🎓', '🎨', '🛡️', '💊'];

function AdminTherapistCategoriesPage() {
  const navigate = useNavigate();
  const authSession = getAuthSession();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'
  const [prescriptionFilter, setPrescriptionFilter] = useState('all'); // 'all', 'required', 'not-required'

  // Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    icon: '🌿',
    displayOrder: 0,
    isActive: true,
    prescriptionPadRequired: false,
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  // Delete Confirmation Modal
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, category: null, loading: false });

  // Notification Toast / Alert
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const showFeedback = (type, message) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback({ type: '', message: '' }), 4000);
  };

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      let res;
      try {
        res = await adminApi.getTherapistCategories({ all: 'true' });
      } catch (adminErr) {
        console.warn('Admin categories endpoint fallback to public endpoint:', adminErr);
        res = await categoryApi.getPublicCategories({ all: 'true' });
      }

      if (res?.success || res?.categories || Array.isArray(res)) {
        const list = res.categories || res.data || (Array.isArray(res) ? res : []);
        setCategories(list);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      showFeedback('error', err.message || 'Failed to load therapist categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Derived Dynamic Real-Time Statistics
  const totalCount = categories.length;
  const activeCount = categories.filter((c) => c.isActive !== false).length;
  const inactiveCount = categories.filter((c) => c.isActive === false).length;
  const prescriptionRequiredCount = categories.filter((c) => Boolean(c.prescriptionPadRequired)).length;
  const prescriptionNotRequiredCount = totalCount - prescriptionRequiredCount;

  // Filtered list for the table display
  const filteredCategories = categories.filter((cat) => {
    const q = searchQuery.trim().toLowerCase();
    const matchesSearch =
      !q ||
      (cat.name && cat.name.toLowerCase().includes(q)) ||
      (cat.slug && cat.slug.toLowerCase().includes(q)) ||
      (cat.description && cat.description.toLowerCase().includes(q));

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && cat.isActive !== false) ||
      (statusFilter === 'inactive' && cat.isActive === false);

    const matchesPrescription =
      prescriptionFilter === 'all' ||
      (prescriptionFilter === 'required' && Boolean(cat.prescriptionPadRequired)) ||
      (prescriptionFilter === 'not-required' && !cat.prescriptionPadRequired);

    return matchesSearch && matchesStatus && matchesPrescription;
  });

  const handleOpenCreateModal = () => {
    setModalMode('create');
    setEditingId(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      icon: '🌿',
      displayOrder: categories.length + 1,
      isActive: true,
      prescriptionPadRequired: false,
    });
    setFormError('');
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (cat) => {
    const id = cat._id || cat.id;
    setModalMode('edit');
    setEditingId(id);
    setFormData({
      name: cat.name || '',
      slug: cat.slug || '',
      description: cat.description || '',
      icon: cat.icon || '🌿',
      displayOrder: cat.displayOrder ?? 0,
      isActive: cat.isActive !== undefined ? cat.isActive : true,
      prescriptionPadRequired: Boolean(cat.prescriptionPadRequired),
    });
    setFormError('');
    setIsModalOpen(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.name.trim()) {
      setFormError('Category name is required');
      return;
    }

    try {
      setFormLoading(true);
      setFormError('');

      const cleanName = formData.name.trim();
      const payload = {
        name: cleanName,
        slug: formData.slug ? formData.slug.trim() : undefined,
        description: formData.description !== undefined ? formData.description.trim() : '',
        icon: formData.icon ? formData.icon.trim() : '🌿',
        displayOrder: Number(formData.displayOrder) || 0,
        isActive: Boolean(formData.isActive),
        prescriptionPadRequired: Boolean(formData.prescriptionPadRequired),
      };

      if (modalMode === 'create') {
        const res = await adminApi.createTherapistCategory(payload);
        showFeedback('success', `Category "${cleanName}" created successfully!`);
        setIsModalOpen(false);
        if (res?.category) {
          setCategories((prev) => [...prev, res.category]);
        }
        fetchCategories();
      } else {
        const catId = editingId;
        if (!catId) {
          throw new Error('Category ID is missing for edit operation');
        }

        // Optimistic state update so cards and table update instantly
        setCategories((prev) =>
          prev.map((c) =>
            (c._id === catId || c.id === catId)
              ? { ...c, ...payload }
              : c
          )
        );

        const res = await adminApi.updateTherapistCategory(catId, payload);
        showFeedback('success', `Category "${cleanName}" updated successfully!`);
        setIsModalOpen(false);
        if (res?.category) {
          setCategories((prev) =>
            prev.map((c) =>
              (c._id === catId || c.id === catId) ? res.category : c
            )
          );
        }
        fetchCategories();
      }
    } catch (err) {
      console.error('Error saving category:', err);
      setFormError(err.message || 'Failed to save category');
      fetchCategories(); // Revert on failure
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleStatus = async (cat) => {
    const catId = cat._id || cat.id;
    const nextStatus = !(cat.isActive !== false);

    // Optimistic instant state update
    setCategories((prev) =>
      prev.map((c) => ((c._id === catId || c.id === catId) ? { ...c, isActive: nextStatus } : c))
    );

    try {
      const res = await adminApi.toggleTherapistCategory(catId);
      const finalStatus = res?.category ? res.category.isActive : nextStatus;
      showFeedback('success', `Category "${cat.name}" is now ${finalStatus ? 'Active' : 'Inactive'}`);
      if (res?.category) {
        setCategories((prev) =>
          prev.map((c) => ((c._id === catId || c.id === catId) ? res.category : c))
        );
      }
    } catch (err) {
      console.error('Error toggling category status:', err);
      showFeedback('error', err.message || 'Failed to toggle status');
      fetchCategories(); // Revert on failure
    }
  };

  const handleTogglePrescription = async (cat) => {
    const catId = cat._id || cat.id;
    const nextReq = !Boolean(cat.prescriptionPadRequired);

    // Optimistic instant state update
    setCategories((prev) =>
      prev.map((c) => ((c._id === catId || c.id === catId) ? { ...c, prescriptionPadRequired: nextReq } : c))
    );

    try {
      let res;
      if (adminApi.toggleTherapistCategoryPrescription) {
        res = await adminApi.toggleTherapistCategoryPrescription(catId);
      } else {
        res = await adminApi.updateTherapistCategory(catId, {
          prescriptionPadRequired: nextReq,
        });
      }

      const finalReq = res?.category ? Boolean(res.category.prescriptionPadRequired) : nextReq;
      showFeedback(
        'success',
        `Prescription Pad is now ${finalReq ? 'Required' : 'Not Required'} for "${cat.name}"`
      );
      if (res?.category) {
        setCategories((prev) =>
          prev.map((c) => ((c._id === catId || c.id === catId) ? res.category : c))
        );
      }
    } catch (err) {
      console.error('Error toggling prescription requirement:', err);
      showFeedback('error', err.message || 'Failed to update prescription requirement');
      fetchCategories(); // Revert on failure
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.category) return;
    const catId = deleteModal.category._id || deleteModal.category.id;
    const catName = deleteModal.category.name;

    try {
      setDeleteModal((prev) => ({ ...prev, loading: true }));
      // Optimistic delete
      setCategories((prev) => prev.filter((c) => (c._id !== catId && c.id !== catId)));

      const res = await adminApi.deleteTherapistCategory(catId);
      showFeedback('success', `Category "${catName}" deleted successfully`);
      setDeleteModal({ isOpen: false, category: null, loading: false });
      fetchCategories();
    } catch (err) {
      console.error('Error deleting category:', err);
      showFeedback('error', err.message || 'Failed to delete category');
      setDeleteModal((prev) => ({ ...prev, loading: false }));
      fetchCategories(); // Revert on failure
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <DashboardSidebar userRole="admin" />

      <main className="sidebar-content-wrapper" style={{ flex: 1, padding: '30px', minHeight: '100vh' }}>
        {/* Top Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '15px' }}>
          <div>
            <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: '#1e293b', margin: '0 0 6px 0' }}>
              🏷️ Therapist Categories & Specializations
            </h1>
            <p style={{ margin: 0, color: '#64748b', fontSize: '0.95rem' }}>
              Configure clinical specialization domains and manage prescription pad requirement policies.
            </p>
          </div>

          <button
            type="button"
            onClick={handleOpenCreateModal}
            style={{
              padding: '12px 24px',
              borderRadius: '12px',
              border: 'none',
              background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
              color: '#ffffff',
              fontWeight: 700,
              fontSize: '0.95rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 4px 14px rgba(124, 58, 237, 0.3)',
              transition: 'all 0.2s ease',
            }}
          >
            <span>➕</span> Add New Category
          </button>
        </div>

        {/* Feedback Alert Banner */}
        {feedback.message && (
          <div
            style={{
              padding: '14px 20px',
              borderRadius: '12px',
              marginBottom: '24px',
              fontWeight: 600,
              fontSize: '0.92rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: feedback.type === 'success' ? '#ecfdf5' : '#fef2f2',
              color: feedback.type === 'success' ? '#065f46' : '#991b1b',
              border: `1.5px solid ${feedback.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{feedback.type === 'success' ? '✅' : '⚠️'}</span>
              <span>{feedback.message}</span>
            </div>
            <button
              onClick={() => setFeedback({ type: '', message: '' })}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontWeight: 'bold', fontSize: '1rem' }}
            >
              ✕
            </button>
          </div>
        )}

        {/* ========================================================================= */}
        {/* SUMMARY METRIC CARDS (REAL-TIME REACTIVE COUNTERS) */}
        {/* ========================================================================= */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '20px', marginBottom: '28px' }}>
          {/* Card 1: Total */}
          <div style={{ background: '#ffffff', padding: '22px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)', transition: 'transform 0.2s' }}>
            <div style={{ color: '#64748b', fontSize: '0.82rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
              TOTAL CATEGORIES
            </div>
            <div style={{ fontSize: '1.9rem', fontWeight: 800, color: '#1e293b' }}>
              {totalCount}
            </div>
          </div>

          {/* Card 2: Active */}
          <div style={{ background: '#ffffff', padding: '22px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)', transition: 'transform 0.2s' }}>
            <div style={{ color: '#059669', fontSize: '0.82rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
              ACTIVE CATEGORIES
            </div>
            <div style={{ fontSize: '1.9rem', fontWeight: 800, color: '#059669' }}>
              {activeCount}
            </div>
          </div>

          {/* Card 3: Prescription Required */}
          <div style={{ background: '#ffffff', padding: '22px', borderRadius: '16px', border: '1.5px solid #ede9fe', boxShadow: '0 4px 12px rgba(124, 58, 237, 0.08)', transition: 'transform 0.2s' }}>
            <div style={{ color: '#7c3aed', fontSize: '0.82rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
              📝 PRESCRIPTION REQUIRED
            </div>
            <div style={{ fontSize: '1.9rem', fontWeight: 800, color: '#7c3aed' }}>
              {prescriptionRequiredCount}
            </div>
          </div>

          {/* Card 4: Prescription Not Required */}
          <div style={{ background: '#ffffff', padding: '22px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)', transition: 'transform 0.2s' }}>
            <div style={{ color: '#475569', fontSize: '0.82rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
              🚫 PRESCRIPTION NOT REQUIRED
            </div>
            <div style={{ fontSize: '1.9rem', fontWeight: 800, color: '#475569' }}>
              {prescriptionNotRequiredCount}
            </div>
          </div>
        </div>

        {/* Search & Filter Toolbar */}
        <div style={{ background: '#ffffff', padding: '18px 24px', borderRadius: '16px', border: '1px solid #e2e8f0', marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          {/* Search Box */}
          <div style={{ position: 'relative', width: '100%', maxWidth: '340px' }}>
            <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>🔍</span>
            <input
              type="text"
              placeholder="Search category, slug, or details..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 16px 10px 40px',
                borderRadius: '10px',
                border: '1.5px solid #cbd5e1',
                fontSize: '0.9rem',
                outline: 'none',
              }}
            />
          </div>

          {/* Filter Controls */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Status Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#f8fafc', padding: '4px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
              <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', paddingLeft: '8px', paddingRight: '4px' }}>Status:</span>
              {['all', 'active', 'inactive'].map((status) => (
                <button
                  key={status}
                  type="button"
                  onClick={() => setStatusFilter(status)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '8px',
                    border: 'none',
                    background: statusFilter === status ? '#7c3aed' : 'transparent',
                    color: statusFilter === status ? '#ffffff' : '#64748b',
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    textTransform: 'capitalize',
                    transition: 'all 0.2s',
                  }}
                >
                  {status === 'all' ? 'All' : status}
                </button>
              ))}
            </div>

            {/* Prescription Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#f8fafc', padding: '4px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
              <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', paddingLeft: '8px', paddingRight: '4px' }}>Prescription:</span>
              {[
                { id: 'all', label: 'All' },
                { id: 'required', label: '📝 Required' },
                { id: 'not-required', label: '🚫 Not Required' },
              ].map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setPrescriptionFilter(item.id)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '8px',
                    border: 'none',
                    background: prescriptionFilter === item.id ? '#6366f1' : 'transparent',
                    color: prescriptionFilter === item.id ? '#ffffff' : '#64748b',
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Categories Table */}
        <div style={{ background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.02)' }}>
          {loading ? (
            <div style={{ padding: '60px', textAlign: 'center' }}>
              <div className="spinner-border text-primary" role="status" style={{ width: '2.5rem', height: '2.5rem', color: '#7c3aed' }}></div>
              <p style={{ marginTop: '14px', color: '#64748b', fontWeight: 600 }}>Loading therapist categories...</p>
            </div>
          ) : filteredCategories.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0', color: '#475569', fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <th style={{ padding: '16px 20px' }}>Category Name</th>
                    <th style={{ padding: '16px 20px' }}>Slug Identifier</th>
                    <th style={{ padding: '16px 20px' }}>Description / Scope</th>
                    <th style={{ padding: '16px 20px', textAlign: 'center' }}>Prescription Pad</th>
                    <th style={{ padding: '16px 20px', textAlign: 'center' }}>Display Order</th>
                    <th style={{ padding: '16px 20px', textAlign: 'center' }}>Status</th>
                    <th style={{ padding: '16px 20px', textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCategories.map((cat) => {
                    const catId = cat._id || cat.id;
                    const isRxReq = Boolean(cat.prescriptionPadRequired);
                    const isCatActive = cat.isActive !== false;

                    return (
                      <tr key={catId} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s' }}>
                        {/* Name & Icon */}
                        <td style={{ padding: '16px 20px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '1.5rem', width: '40px', height: '40px', borderRadius: '10px', background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ede9fe' }}>
                              {cat.icon || '🌿'}
                            </span>
                            <div>
                              <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>
                                {cat.name}
                              </div>
                              <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                                ID: {catId ? catId.slice(-6) : '—'}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Slug */}
                        <td style={{ padding: '16px 20px' }}>
                          <code style={{ background: '#f1f5f9', padding: '4px 8px', borderRadius: '6px', fontSize: '0.82rem', color: '#6366f1', fontWeight: 600 }}>
                            {cat.slug}
                          </code>
                        </td>

                        {/* Description */}
                        <td style={{ padding: '16px 20px', maxWidth: '280px' }}>
                          <span style={{ fontSize: '0.88rem', color: '#64748b', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }} title={cat.description}>
                            {cat.description || '—'}
                          </span>
                        </td>

                        {/* Prescription Pad Requirement Badge & Toggle */}
                        <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                          <button
                            type="button"
                            onClick={() => handleTogglePrescription(cat)}
                            title="Click to toggle Prescription Pad Requirement"
                            style={{
                              padding: '6px 14px',
                              borderRadius: '20px',
                              border: isRxReq ? '1.5px solid #c4b5fd' : '1.5px solid #e2e8f0',
                              fontSize: '0.78rem',
                              fontWeight: 700,
                              cursor: 'pointer',
                              background: isRxReq ? '#f5f3ff' : '#f8fafc',
                              color: isRxReq ? '#6d28d9' : '#64748b',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              boxShadow: isRxReq ? '0 2px 6px rgba(124, 58, 237, 0.12)' : 'none',
                              transition: 'all 0.2s',
                            }}
                          >
                            <span>{isRxReq ? '📝' : '🚫'}</span>
                            <span>{isRxReq ? 'Required' : 'Not Required'}</span>
                          </button>
                        </td>

                        {/* Display Order */}
                        <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                          <span style={{ fontWeight: 700, color: '#334155', fontSize: '0.9rem', background: '#f1f5f9', padding: '3px 10px', borderRadius: '8px' }}>
                            {cat.displayOrder ?? 0}
                          </span>
                        </td>

                        {/* Active Status */}
                        <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                          <button
                            type="button"
                            onClick={() => handleToggleStatus(cat)}
                            style={{
                              padding: '5px 12px',
                              borderRadius: '20px',
                              border: `1px solid ${isCatActive ? '#a7f3d0' : '#cbd5e1'}`,
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              cursor: 'pointer',
                              background: isCatActive ? '#ecfdf5' : '#f1f5f9',
                              color: isCatActive ? '#065f46' : '#64748b',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '5px',
                              transition: 'all 0.2s',
                            }}
                            title="Click to toggle status"
                          >
                            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: isCatActive ? '#10b981' : '#94a3b8' }}></span>
                            {isCatActive ? 'Active' : 'Inactive'}
                          </button>
                        </td>

                        {/* Actions */}
                        <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                          <div style={{ display: 'inline-flex', gap: '8px' }}>
                            <button
                              type="button"
                              onClick={() => handleOpenEditModal(cat)}
                              style={{
                                padding: '6px 12px',
                                borderRadius: '8px',
                                border: '1.5px solid #cbd5e1',
                                background: '#ffffff',
                                color: '#334155',
                                fontSize: '0.82rem',
                                fontWeight: 600,
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                              }}
                            >
                              <span>✏️</span> Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteModal({ isOpen: true, category: cat, loading: false })}
                              style={{
                                padding: '6px 12px',
                                borderRadius: '8px',
                                border: '1.5px solid #fecaca',
                                background: '#fff5f5',
                                color: '#dc2626',
                                fontSize: '0.82rem',
                                fontWeight: 600,
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                              }}
                            >
                              <span>🗑️</span> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ padding: '60px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '14px' }}>🏷️</div>
              <h4 style={{ fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>No categories found</h4>
              <p style={{ color: '#64748b', margin: '0 auto 16px auto', maxWidth: '420px', fontSize: '0.92rem' }}>
                {searchQuery || statusFilter !== 'all' || prescriptionFilter !== 'all'
                  ? 'No categories match the active filters. Try adjusting your search query or filters.'
                  : 'No therapist categories have been configured yet. Create the first one below.'}
              </p>
              <button
                type="button"
                onClick={handleOpenCreateModal}
                style={{
                  padding: '10px 22px',
                  borderRadius: '10px',
                  border: 'none',
                  background: '#7c3aed',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                }}
              >
                ➕ Create Category
              </button>
            </div>
          )}
        </div>
      </main>

      {/* ========================================================================= */}
      {/* CREATE / EDIT CATEGORY MODAL */}
      {/* ========================================================================= */}
      {isModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(5px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          onClick={() => !formLoading && setIsModalOpen(false)}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '20px',
              width: '100%',
              maxWidth: '600px',
              maxHeight: '92vh',
              overflowY: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.3)',
              position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{ padding: '24px 28px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#1e293b', margin: 0 }}>
                  {modalMode === 'create' ? '➕ Add Therapist Category' : '✏️ Edit Therapist Category'}
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                  {modalMode === 'create'
                    ? 'Define a new category and specify clinical prescription pad requirements'
                    : `Updating category: ${formData.name}`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                style={{ background: '#f1f5f9', border: 'none', borderRadius: '50%', width: '34px', height: '34px', cursor: 'pointer', fontSize: '1.1rem', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                ✕
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleFormSubmit} style={{ padding: '24px 28px' }}>
              {formError && (
                <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', color: '#dc2626', fontSize: '0.88rem', marginBottom: '18px', fontWeight: 600 }}>
                  ⚠️ {formError}
                </div>
              )}

              {/* Name Field */}
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                  Category Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Psychiatry & Neuropsych, Family Therapist, Child Psychology"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '10px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '0.95rem',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* Slug Field (Optional) */}
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                  Slug (URL Identifier)
                </label>
                <input
                  type="text"
                  placeholder="e.g. psychiatry-neuropsych (auto-generated if left empty)"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    borderRadius: '10px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '0.88rem',
                    outline: 'none',
                    background: '#f8fafc',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* Icon Picker */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                  Category Icon / Emoji
                </label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
                  <input
                    type="text"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    style={{
                      width: '60px',
                      textAlign: 'center',
                      fontSize: '1.4rem',
                      padding: '8px',
                      borderRadius: '10px',
                      border: '1.5px solid #cbd5e1',
                    }}
                  />
                  <span style={{ fontSize: '0.82rem', color: '#64748b' }}>Select from preset icons below or enter custom emoji</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {POPULAR_ICONS.map((icon) => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => setFormData({ ...formData, icon })}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '8px',
                        border: formData.icon === icon ? '2px solid #7c3aed' : '1px solid #e2e8f0',
                        background: formData.icon === icon ? '#f5f3ff' : '#ffffff',
                        fontSize: '1.2rem',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>

              {/* ========================================================================= */}
              {/* PRESCRIPTION PAD REQUIREMENT CONFIGURATION */}
              {/* ========================================================================= */}
              <div style={{ marginBottom: '22px', background: '#f8fafc', padding: '18px', borderRadius: '14px', border: '1.5px solid #e2e8f0' }}>
                <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: 800, color: '#1e293b', marginBottom: '4px' }}>
                  📋 Prescription Pad Option
                </label>
                <p style={{ margin: '0 0 14px 0', fontSize: '0.82rem', color: '#64748b' }}>
                  Specify whether therapists registered under this category require a medical prescription pad.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  {/* Option 1: Required */}
                  <div
                    onClick={() => setFormData({ ...formData, prescriptionPadRequired: true })}
                    style={{
                      padding: '14px',
                      borderRadius: '12px',
                      border: formData.prescriptionPadRequired ? '2px solid #7c3aed' : '1.5px solid #cbd5e1',
                      background: formData.prescriptionPadRequired ? '#f5f3ff' : '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      boxShadow: formData.prescriptionPadRequired ? '0 4px 12px rgba(124, 58, 237, 0.15)' : 'none',
                    }}
                  >
                    <input
                      type="radio"
                      name="prescriptionPadOption"
                      checked={formData.prescriptionPadRequired === true}
                      onChange={() => setFormData({ ...formData, prescriptionPadRequired: true })}
                      style={{ marginTop: '3px', cursor: 'pointer' }}
                    />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.9rem', color: formData.prescriptionPadRequired ? '#6d28d9' : '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>📝 Required</span>
                      </div>
                      <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '3px', lineHeight: 1.4 }}>
                        Mandatory for medical/clinical specialists (Psychiatrists, Neuropsychiatry) who issue Rx.
                      </div>
                    </div>
                  </div>

                  {/* Option 2: Not Required */}
                  <div
                    onClick={() => setFormData({ ...formData, prescriptionPadRequired: false })}
                    style={{
                      padding: '14px',
                      borderRadius: '12px',
                      border: !formData.prescriptionPadRequired ? '2px solid #6366f1' : '1.5px solid #cbd5e1',
                      background: !formData.prescriptionPadRequired ? '#eef2ff' : '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      boxShadow: !formData.prescriptionPadRequired ? '0 4px 12px rgba(99, 102, 241, 0.15)' : 'none',
                    }}
                  >
                    <input
                      type="radio"
                      name="prescriptionPadOption"
                      checked={formData.prescriptionPadRequired === false}
                      onChange={() => setFormData({ ...formData, prescriptionPadRequired: false })}
                      style={{ marginTop: '3px', cursor: 'pointer' }}
                    />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.9rem', color: !formData.prescriptionPadRequired ? '#4338ca' : '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>🚫 Not Required</span>
                      </div>
                      <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '3px', lineHeight: 1.4 }}>
                        For talk therapy, counseling, wellness, and psychotherapy where Rx is not provided.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Description Field */}
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                  Description (Scope of Care)
                </label>
                <textarea
                  rows="3"
                  placeholder="Provide a short overview of clinical scope, targeted issues, and treatment methodologies..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '0.9rem',
                    outline: 'none',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                  }}
                ></textarea>
              </div>

              {/* Display Order & Active Checkbox */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={formData.displayOrder}
                    onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '10px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '0.9rem',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', paddingTop: '24px' }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.92rem', fontWeight: 600, color: '#1e293b' }}>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    Active / Visible in Directory
                  </label>
                </div>
              </div>

              {/* Modal Footer */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', borderTop: '1px solid #f1f5f9', paddingTop: '20px' }}>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  disabled={formLoading}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '10px',
                    border: '1.5px solid #cbd5e1',
                    background: '#ffffff',
                    color: '#475569',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '10px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
                    color: '#ffffff',
                    fontWeight: 700,
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(124, 58, 237, 0.25)',
                  }}
                >
                  {formLoading ? 'Saving...' : modalMode === 'create' ? 'Create Category' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* DELETE CONFIRMATION MODAL */}
      {/* ========================================================================= */}
      {deleteModal.isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(5px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          onClick={() => !deleteModal.loading && setDeleteModal({ isOpen: false, category: null, loading: false })}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '20px',
              width: '100%',
              maxWidth: '440px',
              padding: '28px',
              textAlign: 'center',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>🗑️</div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#1e293b', marginBottom: '8px' }}>
              Delete Category?
            </h3>
            <p style={{ color: '#64748b', fontSize: '0.92rem', lineHeight: 1.5, marginBottom: '24px' }}>
              Are you sure you want to delete category <strong>"{deleteModal.category?.name}"</strong>? This will permanently remove this specialization category.
            </p>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={() => setDeleteModal({ isOpen: false, category: null, loading: false })}
                disabled={deleteModal.loading}
                style={{
                  padding: '10px 20px',
                  borderRadius: '10px',
                  border: '1.5px solid #cbd5e1',
                  background: '#ffffff',
                  color: '#475569',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={deleteModal.loading}
                style={{
                  padding: '10px 24px',
                  borderRadius: '10px',
                  border: 'none',
                  background: '#dc2626',
                  color: '#ffffff',
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(220, 38, 38, 0.25)',
                }}
              >
                {deleteModal.loading ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminTherapistCategoriesPage;
